import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCheck, FaFileDownload, FaSpinner, FaUpload, FaUserCog, FaUserGraduate, FaUserTie } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { TEMPLATE_CONFIGS, downloadCleanTemplate, validateCSVData, remapCSVForBackend } from '../utils/simpleCsvTemplates';
import { formatApiError } from '../utils/apiErrorFormatter';
import '../styles/CreateUserFormModern.css';
import '../styles/ImportUsers.css';

const MODES = [
  { id: 'students', label: 'Students', icon: FaUserGraduate, color: '#4a90e2', hint: 'Student CSV import' },
  { id: 'preceptors', label: 'Preceptors', icon: FaUserTie, color: '#f59e0b', hint: 'Preceptor CSV import' },
  { id: 'admins', label: 'Admins', icon: FaUserCog, color: '#10b981', hint: 'Administrator CSV import' }
];

const ImportUsers = ({ initialMode = 'students' }) => {
  const [mode, setMode] = useState(initialMode || 'students');
  const [file, setFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importStats, setImportStats] = useState(null);
  const fileInputRef = useRef(null);

  const currentMode = useMemo(() => MODES.find((item) => item.id === mode) || MODES[0], [mode]);
  const templateConfig = TEMPLATE_CONFIGS[mode] || TEMPLATE_CONFIGS.students;

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode);
      setFile(null);
      setValidationResults(null);
      setError('');
      setSuccess('');
      setImportStats(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [initialMode]);

  const resetUploadState = () => {
    setFile(null);
    setValidationResults(null);
    setError('');
    setSuccess('');
    setImportStats(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    resetUploadState();
  };

  const readFileContent = (selectedFile) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new Error('Failed to read CSV file'));
    reader.readAsText(selectedFile);
  });

  const handleTemplateDownload = () => {
    try {
      downloadCleanTemplate(mode);
    } catch (downloadError) {
      setError(downloadError.message || 'Failed to download template');
    }
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      resetUploadState();
      return;
    }

    setValidating(true);
    setError('');
    setSuccess('');
    setImportStats(null);

    try {
      const content = await readFileContent(selectedFile);
      const results = validateCSVData(content, mode);
      setFile(selectedFile);
      setValidationResults(results);
    } catch (validationError) {
      setFile(selectedFile);
      setValidationResults({
        valid: [],
        invalid: [],
        summary: {
          total: 0,
          valid: 0,
          invalid: 0,
          errors: [validationError.message]
        },
        parseError: true
      });
      setError(validationError.message || 'Invalid CSV file');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setImportStats(null);

    if (!file || !validationResults || validationResults.summary.valid === 0) {
      setError('Upload a valid CSV file before importing.');
      setLoading(false);
      return;
    }

    const rawContent = await readFileContent(file);
    const remappedBlob = remapCSVForBackend(rawContent, mode);
    const remappedFile = new File([remappedBlob], file.name, { type: 'text/csv' });

    const formData = new FormData();
    formData.append('file', remappedFile);

    const endpoint = mode === 'students'
      ? '/csv/import_student_csv'
      : mode === 'preceptors'
        ? '/csv/import_preceptor_csv'
        : '/csv/import_admin_csv';

    try {
      const response = await axiosInstance.post(endpoint, formData);
      const data = response.data;

      setSuccess(`Imported ${data?.total_inserted || 0} ${mode.slice(0, -1)}(s) successfully.`);
      setImportStats({
        processed: data?.total_processed || 0,
        inserted: data?.total_inserted || 0,
        emailsSent: data?.email_results?.filter((result) => result.email_sent).length || 0
      });

      setFile(null);
      setValidationResults(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (submitError) {
      setError(formatApiError(submitError, 'Import failed'));
    } finally {
      setLoading(false);
    }
  };

  const canImport = Boolean(file && validationResults && validationResults.summary.valid > 0 && !loading && !validating);

  return (
    <div className="cuf-page iu-page">
      <section className="cuf-shell iu-shell">
        <section className="cuf-form-panel iu-panel">
          <header className="cuf-header iu-header">
            <div className="cuf-header-main iu-header-main">
              <div className="cuf-header-icon iu-header-icon"><FaUpload /></div>
              <div>
                <h2>Import Users</h2>
                <p>Download template, fill in records, upload the CSV to import.</p>
              </div>
            </div>
          </header>

          <form className="cuf-form import-users-form" onSubmit={handleSubmit}>
            <section className="cuf-section">
              <div className="cuf-section-title">
                <FaUserCog />
                <h3>Choose import type</h3>
              </div>
              <div className="cuf-type-grid import-mode-grid">
                {MODES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`cuf-type-card import-mode-card ${mode === item.id ? 'is-active' : ''}`}
                      onClick={() => handleModeChange(item.id)}
                      style={{ '--mode-color': item.color }}
                    >
                      <span className="cuf-type-card__icon"><Icon /></span>
                      <span className="import-mode-card__content">
                        <strong>{item.label}</strong>
                        <small>{item.hint}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="iu-steps">
              <div className="iu-step-card">
                <div className="iu-step-badge">1</div>
                <div className="iu-step-content">
                  <h4>Get the template</h4>
                  <p>Use the correct CSV structure for {currentMode.label.toLowerCase()}.</p>
                  <button type="button" className="iu-step-button iu-step-button--secondary" onClick={handleTemplateDownload}>
                    <FaFileDownload /> Download template
                  </button>
                  <div className="iu-note">Template: {templateConfig.title}</div>
                </div>
              </div>

              <div className="iu-step-card">
                <div className="iu-step-badge">2</div>
                <div className="iu-step-content">
                  <h4>Upload your CSV</h4>
                  <p>Pick the completed file and we’ll check it right away.</p>
                  <label className="iu-file-button" htmlFor="import-users-file">
                    <FaUpload /> {file ? 'Replace file' : 'Choose CSV file'}
                  </label>
                  <input
                    ref={fileInputRef}
                    id="import-users-file"
                    className="iu-file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                  />
                  {file && (
                    <div className="iu-file-chip">
                      <FaCheck />
                      <span>{file.name}</span>
                      {validating && <em>Checking…</em>}
                    </div>
                  )}
                </div>
              </div>

              <div className="iu-step-card">
                <div className="iu-step-badge">3</div>
                <div className="iu-step-content">
                  <h4>Import</h4>
                  <p>Confirm the file is ready, then start the import.</p>
                  {validationResults?.summary && (
                    <div className={`iu-status ${validationResults.summary.invalid === 0 ? 'is-ready' : 'is-warn'}`}>
                      {validationResults.summary.invalid === 0 ? 'Ready to import' : 'Fix the file and upload again'}
                      <span>
                        {validationResults.summary.valid} valid rows
                        {validationResults.summary.invalid > 0 ? ` • ${validationResults.summary.invalid} invalid` : ''}
                      </span>
                    </div>
                  )}

                  {validationResults?.summary?.invalid > 0 && (
                    <div className="iu-validation-errors" style={{ marginTop: '0.75rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#b45309', marginBottom: '0.35rem' }}>
                        What to fix (first few issues)
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#92400e', fontSize: '0.85rem' }}>
                        {(validationResults.summary.errors || []).slice(0, 6).map((msg, idx) => (
                          <li key={idx}>{msg}</li>
                        ))}
                      </ul>
                      {(validationResults.summary.errors || []).length > 6 && (
                        <div style={{ marginTop: '0.35rem', color: '#92400e', fontSize: '0.8rem' }}>
                          Showing 6 of {(validationResults.summary.errors || []).length} issues.
                        </div>
                      )}
                    </div>
                  )}
                  <button type="submit" className="iu-step-button" disabled={!canImport}>
                    {loading ? <><FaSpinner className="spin" /> Importing...</> : 'Import now'}
                  </button>
                </div>
              </div>
            </section>

            {error && <div className="msg-error iu-message">{error}</div>}
            {success && <div className="msg-success iu-message">{success}</div>}

            {importStats && (
              <div className="iu-summary">
                <div className="iu-summary-card">
                  <strong>{importStats.processed}</strong>
                  <span>Processed</span>
                </div>
                <div className="iu-summary-card">
                  <strong>{importStats.inserted}</strong>
                  <span>Imported</span>
                </div>
                {importStats.emailsSent > 0 && (
                  <div className="iu-summary-card">
                    <strong>{importStats.emailsSent}</strong>
                    <span>Emails sent</span>
                  </div>
                )}
              </div>
            )}
          </form>
        </section>
      </section>
    </div>
  );
};

export default ImportUsers;
